// Zod schemas for the live planes the dashboard consumes:
//   1. Event bus  — WS /ws/events (preferred) or SSE GET /api/v1/events.
//      FROZEN envelope: every frame is { type, ts, id, data } where `ts` is a
//      unix-ms number, `id` is a stable event id, and `type` is the
//      discriminator. `data` carries the typed payload. The `type` catalog and
//      the snake_case `data` shapes are FROZEN by the server (see
//      ../../mcpgraph-gateway/SPEC.md "Frozen event-bus envelope"); these schemas
//      match them EXACTLY.
//   2. Stream WS  — WS /streams/{id}?token=...: producer-stream frames mirroring
//      proto/data.proto. FROZEN JSON projection:
//        chunk  = { stream_id, seq, payload, encoding, channel_id?, keyframe?, eos?, pts_ms? }
//        status = { stream_id, state, bytes, seq, error? }
//      `encoding` ∈ base64|json (the WS carries JSON, not protobuf). Chunks are
//      distinguished from status frames structurally (a status carries `state`,
//      a chunk carries `payload`); there is no `type` tag on stream frames.
//      `channel_id` (proto-additive: data.proto StreamChunk.channel_id) routes a
//      frame to its DOWN channel; 0 = the single DOWN channel (simple producer).
//
// EVERY frame is parsed through these; malformed frames are dropped, never
// allowed to crash the UI (SPEC "definition of done").

import { z } from 'zod';
import {
  ChannelList,
  ClientStatus,
  GpuStat,
  McpSession,
  NodeProfile,
  PublicationStatus,
  StreamState,
  TaskState,
} from './schemas';

// --- frozen event-bus `data` payload shapes ------------------------------

// task.updated
export const TaskUpdatedData = z.object({
  task_id: z.string(),
  canonical_tool: z.string(),
  network_id: z.string().optional(),
  state: TaskState.catch('QUEUED'),
  priority: z.number().int().default(0),
  fraction: z.number().optional(),
  provider_instance_id: z.string().optional(),
});
export type TaskUpdatedData = z.infer<typeof TaskUpdatedData>;

// call.logged — NB: the bus uses `status` (call outcome) + flat `gpu_seconds`,
// unlike the REST CallLog which uses `state` + a nested `usage`. Kept as a loose
// string so a server-side status vocabulary we don't enumerate still validates.
export const CallLoggedData = z.object({
  call_id: z.string(),
  canonical_tool: z.string(),
  token_id: z.string().optional(),
  status: z.string(),
  duration_ms: z.number().optional(),
  gpu_seconds: z.number().optional(),
});
export type CallLoggedData = z.infer<typeof CallLoggedData>;

// queue.changed
export const QueueChangedData = z.object({
  canonical_tool: z.string(),
  depth: z.number().int().default(0),
  oldest_wait_ms: z.number().optional(),
});
export type QueueChangedData = z.infer<typeof QueueChangedData>;

// stream.opened / stream.closed — carries the channel descriptor list.
export const StreamEventData = z.object({
  stream_id: z.string(),
  canonical_tool: z.string().optional(),
  channels: ChannelList,
  consumer_count: z.number().int().default(0),
});
export type StreamEventData = z.infer<typeof StreamEventData>;

// publication.changed
export const PublicationChangedData = z.object({
  publication_id: z.string(),
  network_id: z.string(),
  service_slug: z.string().optional(),
  status: PublicationStatus.catch('pending'),
});
export type PublicationChangedData = z.infer<typeof PublicationChangedData>;

// client.status
export const ClientStatusData = z.object({
  client_id: z.string(),
  status: ClientStatus.catch('offline'),
  node_profile: NodeProfile.optional(),
});
export type ClientStatusData = z.infer<typeof ClientStatusData>;

// client.metrics — gpus are a leaner per-index projection, host stats are nested
// under `host` (distinct from the REST NodeProfile shape).
export const ClientMetricsGpu = z.object({
  index: z.number().int(),
  // name + temp_c now carried on the bus so the live overlay keeps full GPU identity/telemetry
  // (older frames omitted them; defaults keep backward compat with pre-fix gateways).
  name: z.string().default(''),
  vram_total_mb: z.number().int().default(0),
  vram_free_mb: z.number().int().default(0),
  util_pct: z.number().int().default(0),
  temp_c: z.number().int().default(0),
});
// ST43 — node object-store / disk occupancy carried additively on the heartbeat
// (older gateways omit it; the store block stays optional). `bytes` is the store's
// own footprint; disk_free/disk_total are the store_dir filesystem figures.
export const ClientStoreMetrics = z.object({
  bytes: z.number().int().optional(),
  objects: z.number().int().optional(),
  disk_free: z.number().int().optional(),
  disk_total: z.number().int().optional(),
});
export type ClientStoreMetrics = z.infer<typeof ClientStoreMetrics>;

export const ClientMetricsData = z.object({
  client_id: z.string(),
  gpus: z.array(ClientMetricsGpu).default([]),
  host: z
    .object({
      cpu_pct: z.number().default(0),
      ram_used_mb: z.number().int().default(0),
      ram_total_mb: z.number().int().default(0),
    })
    .optional(),
  store: ClientStoreMetrics.optional(),
});
export type ClientMetricsData = z.infer<typeof ClientMetricsData>;

// object.changed — projection of the control-plane ObjectEvent into the bus.
export const ObjectOp = z.enum(['STORED', 'DELETED']);
export type ObjectOp = z.infer<typeof ObjectOp>;
export const ObjectChangedData = z.object({
  s3_key: z.string(),
  network_id: z.string().optional(),
  op: ObjectOp.catch('STORED'),
  instance: z.string().optional(),
  size: z.number().int().optional(),
  is_global: z.boolean().optional(),
});
export type ObjectChangedData = z.infer<typeof ObjectChangedData>;

// route.instantiated / route.completed / route.errored (ADR-020 R2/R7). A route
// instance's lifecycle on the bus: `instantiated` carries the pinned version + hop
// canonical-tool chain; `errored` carries the failure reason. Shared shape.
export const RouteEventData = z.object({
  instance_id: z.string(),
  route_id: z.string(),
  route_name: z.string(),
  network_id: z.string(),
  version: z.number().int().default(1),
  hops: z.array(z.string()).default([]),
  error: z.string().optional(),
});
export type RouteEventData = z.infer<typeof RouteEventData>;

// task.completed (ST42) — ADDITIVE to the frozen catalog. Carries the terminal
// task outcome so an open task drawer can show the result WITHOUT a refetch. The
// result is freeform (dict / list / string), output_refs is a list of object refs,
// error is the failure reason. All optional so a lean payload still validates.
export const TaskCompletedData = z.object({
  task_id: z.string(),
  canonical_tool: z.string().optional(),
  network_id: z.string().optional(),
  state: TaskState.catch('SUCCEEDED'),
  result: z.unknown().optional(),
  output_refs: z.array(z.string()).default([]),
  error: z.string().optional(),
});
export type TaskCompletedData = z.infer<typeof TaskCompletedData>;

// session.opened — `data` mirrors a GET /api/v1/activity/mcp/sessions row (the
// SAME object api.mcpSessions() returns per session), keyed by session_id.
export const SessionOpenedData = McpSession;
export type SessionOpenedData = z.infer<typeof SessionOpenedData>;

// session.closed — just the id of the session that went away.
export const SessionClosedData = z.object({ session_id: z.string() });
export type SessionClosedData = z.infer<typeof SessionClosedData>;

// --- frozen event-bus envelope -------------------------------------------

/** Build a frozen-envelope frame schema for a given `type` + `data` payload. */
function envelope<TLit extends string, S extends z.ZodTypeAny>(type: TLit, data: S) {
  return z.object({
    type: z.literal(type),
    ts: z.number(), // unix_ms
    id: z.string(),
    data,
  });
}

export const TaskFrame = envelope('task.updated', TaskUpdatedData);
export const TaskCompletedFrame = envelope('task.completed', TaskCompletedData);
export const CallFrame = envelope('call.logged', CallLoggedData);
export const QueueFrame = envelope('queue.changed', QueueChangedData);
export const StreamOpenedFrame = envelope('stream.opened', StreamEventData);
export const StreamClosedFrame = envelope('stream.closed', StreamEventData);
export const PublicationFrame = envelope('publication.changed', PublicationChangedData);
export const ClientStatusFrame = envelope('client.status', ClientStatusData);
export const ClientMetricsFrame = envelope('client.metrics', ClientMetricsData);
export const ObjectFrame = envelope('object.changed', ObjectChangedData);
export const RouteInstantiatedFrame = envelope('route.instantiated', RouteEventData);
export const RouteCompletedFrame = envelope('route.completed', RouteEventData);
export const RouteErroredFrame = envelope('route.errored', RouteEventData);
export const SessionOpenedFrame = envelope('session.opened', SessionOpenedData);
export const SessionClosedFrame = envelope('session.closed', SessionClosedData);

// The FROZEN catalog: exactly these frame types ride the bus (incl. route.* — ADR-020).
export const EventFrame = z.discriminatedUnion('type', [
  TaskFrame,
  TaskCompletedFrame,
  CallFrame,
  QueueFrame,
  StreamOpenedFrame,
  StreamClosedFrame,
  PublicationFrame,
  ClientStatusFrame,
  ClientMetricsFrame,
  ObjectFrame,
  RouteInstantiatedFrame,
  RouteCompletedFrame,
  RouteErroredFrame,
  SessionOpenedFrame,
  SessionClosedFrame,
]);
export type EventFrame = z.infer<typeof EventFrame>;
export type EventType = EventFrame['type'];

// --- stream WS frames (WS /streams/{id}) ---------------------------------

// A media/data chunk. `payload` is base64 for binary media or a JSON string for
// structured streams (e.g. token streaming). `encoding` disambiguates.
// `channel_id` routes the frame to its DOWN channel in a multi-channel session.
export const StreamChunkFrame = z.object({
  stream_id: z.string(),
  seq: z.number().int(),
  payload: z.string(),
  encoding: z.enum(['base64', 'json']).default('base64'),
  channel_id: z.number().int().default(0),
  keyframe: z.boolean().default(false),
  eos: z.boolean().default(false),
  pts_ms: z.number().optional(),
});
export type StreamChunkFrame = z.infer<typeof StreamChunkFrame>;

// Lifecycle/status frame from the server fan-out (mirrors StreamStatus).
export const StreamStatusFrame = z.object({
  stream_id: z.string(),
  state: StreamState,
  bytes: z.number().int().default(0),
  seq: z.number().int().default(0),
  error: z.string().optional(),
});
export type StreamStatusFrame = z.infer<typeof StreamStatusFrame>;

// No `type` tag on the wire: a status frame carries `state`, a chunk carries
// `payload`. Status is tried first so a frame with a valid `state` is treated as
// a status even if it also carried bytes.
export const StreamWsFrame = z.union([StreamStatusFrame, StreamChunkFrame]);
export type StreamWsFrame = z.infer<typeof StreamWsFrame>;

/** Type guard: is this a status frame (vs a data chunk)? */
export function isStreamStatus(f: StreamWsFrame): f is StreamStatusFrame {
  return 'state' in f && !('payload' in f);
}

// An UP-channel frame the consumer SENDS to the tool over the same bidirectional
// WS (consumer->tool). Mirrors a StreamChunk with the UP channel_id set; the
// server delivers it to the producer as StreamControl.inbound (proto-additive).
export interface OutboundUpFrame {
  stream_id: string;
  seq: number;
  channel_id: number;
  payload: string;
  encoding: 'base64' | 'json';
  eos?: boolean;
  pts_ms?: number;
}

// Re-exported so consumers keep a single import site.
export { GpuStat };

// --- stream-frame payload decoder ----------------------------------------

export type DecodedPayload =
  | { kind: 'binary'; bytes: Uint8Array }
  | { kind: 'json'; value: unknown };

/**
 * Decode a stream chunk's payload per its `encoding`:
 *  - base64 -> raw bytes (Uint8Array) for media rendering / metering
 *  - json   -> parsed JSON value
 * Never throws: a malformed payload yields empty bytes / null json so the live
 * renderer degrades gracefully instead of crashing.
 */
export function decodeStreamPayload(frame: StreamChunkFrame): DecodedPayload {
  if (frame.encoding === 'json') {
    try {
      return { kind: 'json', value: JSON.parse(frame.payload) };
    } catch {
      return { kind: 'json', value: null };
    }
  }
  return { kind: 'binary', bytes: base64ToBytes(frame.payload) };
}

/** base64 -> Uint8Array, working in both browser (atob) and Node (Buffer). */
export function base64ToBytes(b64: string): Uint8Array {
  if (!b64) return new Uint8Array(0);
  try {
    if (typeof atob !== 'undefined') {
      const bin = atob(b64);
      const out = new Uint8Array(bin.length);
      for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
      return out;
    }
    // Node fallback.
    return new Uint8Array(Buffer.from(b64, 'base64'));
  } catch {
    return new Uint8Array(0);
  }
}

// --- safe parse helpers --------------------------------------------------

export interface ParseOk<T> {
  ok: true;
  value: T;
}
export interface ParseErr {
  ok: false;
  error: z.ZodError;
}
export type ParseResult<T> = ParseOk<T> | ParseErr;

function safe<S extends z.ZodTypeAny>(schema: S, raw: unknown): ParseResult<z.infer<S>> {
  const r = schema.safeParse(raw);
  return r.success ? { ok: true, value: r.data } : { ok: false, error: r.error };
}

/** Parse an event-bus frame from a raw WS/SSE string or object. */
export function parseEventFrame(raw: unknown): ParseResult<EventFrame> {
  const obj = typeof raw === 'string' ? tryJson(raw) : raw;
  if (obj === undefined) {
    return { ok: false, error: new z.ZodError([]) };
  }
  return safe(EventFrame, obj);
}

/** Parse a stream WS frame from a raw WS string or object. */
export function parseStreamFrame(raw: unknown): ParseResult<StreamWsFrame> {
  const obj = typeof raw === 'string' ? tryJson(raw) : raw;
  if (obj === undefined) {
    return { ok: false, error: new z.ZodError([]) };
  }
  return safe(StreamWsFrame, obj);
}

function tryJson(s: string): unknown | undefined {
  try {
    return JSON.parse(s);
  } catch {
    return undefined;
  }
}
